'use client';

import { AtHeader, AtTextInput, AtClickable } from '@/components/atoms';
import {
  EClickableVariants,
  EClickableTypes,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { AdvancedFiltersBodyProps, RangeKey } from '../types';
import { numbersOnly, zeroOr, validatePair, PAIRS } from '../utils';
import { useRouter } from 'next/navigation';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export function Combobox({ scrollListData }: any) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const plansRaw = scrollListData.map(
    (scrollListItem: any) => scrollListItem.Plan.Name,
  ) as string[];
  const plans = [...new Set(plansRaw)].sort((a, b) => a.localeCompare(b));

  const router = useRouter();

  function nonFuzzySearch(value: string, search: string) {
    const lowerValue = value.toLowerCase();
    const lowerSearch = search.toLowerCase();

    return lowerValue.startsWith(lowerSearch) ? 1 : 0;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild className="w-full">
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="mt-[1.25rem] min-h-[60px] w-full justify-between rounded-[.938rem] border-2 border-[var(--color-ui-stroke-secondary)] border-skin-stroke-secondary bg-skin-primary text-base font-normal shadow-none md:border-[var(--color-ui-stroke-primary)]"
        >
          {value ? value : 'Select plan...'}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] overflow-hidden rounded-[.938rem] border-2 border-[var(--color-ui-stroke-secondary)] bg-skin-primary p-0 md:border-[var(--color-ui-stroke-primary)]">
        <Command filter={nonFuzzySearch}>
          <CommandInput
            placeholder="Search plans..."
            className="h-9 text-base"
          />
          <CommandList>
            <CommandEmpty>No plans found.</CommandEmpty>
            <CommandGroup>
              {plans.map((plan) => (
                <CommandItem
                  key={plan}
                  value={plan}
                  onSelect={(currentValue) => {
                    setValue(currentValue);
                    setOpen(false);
                    const route = currentValue
                      .toLowerCase()
                      .replace(/[']/g, '')
                      .replace(/[\s]/g, '-');
                    router.push(`/house-plans/${route}`);
                  }}
                  className="text-base"
                >
                  {plan}
                  <Check
                    className={cn(
                      'ml-auto',
                      value === plan ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const AdvancedFiltersBody = ({
  scrollListData,
  values,
  configData,
  onChange,
  onBlur,
  onApply,
  onValidityChange,
  onErrorsChange,
}: AdvancedFiltersBodyProps) => {
  const setField =
    (key: RangeKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(key, numbersOnly(e.target.value));
    };

  const handleBlur =
    (key: RangeKey) => (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(key, zeroOr(numbersOnly(e.target.value)));
    };

  const labelOf = useCallback(
    (key: RangeKey) =>
      configData.find((c: any) => c.key === key)?.label ??
      (key.includes('Min') ? 'Minimum' : 'Maximum'),
    [configData],
  );

  const errors = useMemo(() => {
    const d = validatePair(values.MinDepth, values.MaxDepth);
    const w = validatePair(values.MinWidth, values.MaxWidth);
    return {
      MinDepth: d.minErr,
      MaxDepth: d.maxErr,
      MinWidth: w.minErr,
      MaxWidth: w.maxErr,
    } as Record<RangeKey, string>;
  }, [values]);

  const hasErrors = useMemo(
    () => Object.values(errors).some((msg) => msg.length > 0),
    [errors],
  );

  useEffect(() => {
    onValidityChange?.(hasErrors);
  }, [hasErrors, onValidityChange]);

  useEffect(() => {
    onErrorsChange?.(errors);
  }, [errors, onErrorsChange]);

  return (
    <div className="mt-8 md:m-0 md:px-20 md:py-5">
      {scrollListData && (
        <div className="mb-10">
          <AtHeader element="h4" variant="h6" alt className="mb-5">
            Go to a House Plan
          </AtHeader>

          <Combobox scrollListData={scrollListData} />
        </div>
      )}

      <div className="mb-10">
        {PAIRS.map(({ title, min, max }) => (
          <div className="mb-5" key={title}>
            <AtHeader element="h4" variant="h6" alt className="mb-5">
              {title}
            </AtHeader>

            <div className="flex flex-col gap-8 sm:flex-row">
              <AtTextInput
                id={min}
                value={values[min]}
                onChange={setField(min)}
                onBlur={handleBlur(min)}
                label={labelOf(min)}
                error={errors[min]}
                inputMode="numeric"
                aria-invalid={Boolean(errors[min])}
                className="min-h-[60px]"
              />

              <AtTextInput
                id={max}
                value={values[max]}
                onChange={setField(max)}
                onBlur={handleBlur(max)}
                label={labelOf(max)}
                error={errors[max]}
                inputMode="numeric"
                aria-invalid={Boolean(errors[max])}
                className="min-h-[60px]"
              />
            </div>
          </div>
        ))}
      </div>
      {onApply && (
        <div className="flex justify-center">
          <AtClickable
            label="Apply"
            variant={EClickableVariants.PRIMARY}
            outline
            type={EClickableTypes.CTA}
            interactLocation="house-plan-controls"
            onClick={!hasErrors ? onApply : undefined}
          />
        </div>
      )}
    </div>
  );
};
